import { ToastrService } from 'ngx-toastr';
import { tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { SnCamera } from 'libs/api/entities/src/lib/camera';

@Injectable({ providedIn: 'root' })
export class CameraDetectionsService {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
  ) {}

  public addDetection(detectionData: any, camera: SnCamera): any {
    const aiDetectionTypes = this._formatData(detectionData, camera);

    return this._httpClient
      .patch(`/api/cameras/${camera.id}/`, { aiDetectionTypes })
      .pipe(tap(() => this._toastrService.success('Camera updated')));
  }

  private _formatData(detectionData: any, camera: SnCamera): object[] {
    const data = this._generateData(detectionData);
    switch (detectionData.name.ruleType) {
      case 'vca_smart_motion_detection':
        return [
          ...camera.aiDetectionTypes,
          {
            ...data,
            id: 'cfb58f96-5b68-4a57-8f05-585b16d85822',
            extraParams: {
              roi: [],
              schedule: {
                mode: 'Time',
                time: {
                  begin: this._convertToMilitaryTime(detectionData.data.smartMotionDetectionStart),
                  end: this._convertToMilitaryTime(detectionData.data.smartMotionDetectionEnd),
                },
                enable: true,
              },
              scheduledRoi: [
                {
                  name: 'Motion Detection Scheduled Generic',
                  id: 0,
                  itemSize: 10,
                  humanDetection: {
                    enable: detectionData.data.peopleDetection || false,
                  },
                  polygonStd: detectionData.gridPoints,
                  temporalFilter: {
                    minActivityDuration: detectionData.data.minActivityDur,
                    activityMergeInterval: detectionData.data.activityMergeInt,
                    enable: true,
                  },
                  vehicleDetection: {
                    enable: detectionData.data.vehicleDetection || false,
                  },
                },
              ],
              timezone: null,
            },
          },
        ];
      case 'vca_crowd_detection':
        return [
          ...camera.aiDetectionTypes,
          {
            ...data,
            id: '5109dfdb-fb87-4ec6-8618-78ecfd09731a',
            extraParams: {
              leaveDelay: detectionData.data.leftZone,
              enterDelay: detectionData.data.enteredZone,
              ruleName: detectionData.data.triggerName,
              field: [detectionData.gridPoints],
              peopleNumber: detectionData.data.peopleNumber,
              schedule: detectionData.schedule,
              timezone: 'America/New_York',
            },
          },
        ];
      case 'vca_loitering_detection':
        return [
          ...camera.aiDetectionTypes,
          {
            ...data,
            id: 'fb5aaffe-0b76-4368-b31c-8ccf66d2e45d',
            extraParams: {
              schedule: detectionData.schedule,
              ruleName: detectionData.data.triggerName,
              mode: 'Repeated',
              stayTime: detectionData.data.loiteringTriggerCondition,
              ruleZoomIn: true,
              field: [detectionData.gridPoints],
              field3D: [],
            },
          },
        ];
      case 'vca_line_crossing_detection':
        return [
          ...camera.aiDetectionTypes,
          {
            ...data,
            id: '3b26a552-52ba-4950-954e-cce6bad9e764',
            extraParams: {
              schedule: detectionData.schedule,
              ruleName: detectionData.data.triggerName,
              mode: 'Repeated',
              ruleZoomIn: true,
              line: [detectionData.gridPoints],
              line3D: [],
              direction: detectionData.data.lineCrossingTriggerCondition,
            },
          },
        ];
      case 'vca_intrusion_detection':
        return [
          ...camera.aiDetectionTypes,
          {
            ...data,
            id: 'fdca7bf2-82a7-42cb-a6f2-6e2f041bdf01',
            extraParams: {
              schedule: detectionData.schedule,
              ruleName: detectionData.data.triggerName,
              mode: 'Repeated',
              walkingDirection: detectionData.data.intrusionTriggerCondition,
              field: [detectionData.gridPoints],
              field3D: [],
              ruleZoomIn: true,
            },
          },
        ];
      default:
        break;
    }
  }

  private _generateData(detectionData: any): any {
    return {
      name: detectionData.name.ruleName,
      type: detectionData.name.ruleType,
      confidence: 50,
      labels: [],
      allowedLabels: [],
      schedules: [],
      roi: [],
      meta: {},
      postDetection: {},
      detectionTimeout: 0,
    };
  }

  private _convertToMilitaryTime(time: string): string {
    let newTime: string;
    if (!time.includes('AM' || 'PM')) {
      return time;
    }
    const timeArray = time.split(':');
    let hours = timeArray[0];
    if (hours.length === 1) {
      hours = '0' + hours;
    }
    const minutes = timeArray[1];
    if (time.includes('AM')) {
      const returnMins = minutes.split(' ');
      const mins = returnMins[0];
      return (newTime = hours + ':' + mins);
    }
    if (time.includes('PM')) {
      const returnMins = minutes.split(' ');
      const mins = returnMins[0];
      const militaryHours = Number(hours) + 12;
      return (newTime = militaryHours.toString() + ':' + mins);
    }
    return newTime;
  }
}
